import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { SubizaLogo } from "./subiza-logo";
import { ThemeToggle } from "./theme-toggle";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import {
  LogOut,
  LayoutDashboard,
  Shield,
  Home,
  LogIn,
  Gamepad2,
  ShieldAlert,
  Users,
  Menu,
} from "lucide-react";

const ACTIVE_PROPS = { className: "bg-surface-hover text-text" };

export function Header() {
  const { user, isAdmin, isSuperAdmin, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = [
    { to: "/", label: "Home", icon: Home, exact: true },
    { to: "/founders", label: "Founders", icon: Users },
    ...(user ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }] : []),
    ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: Shield }] : []),
    ...(isSuperAdmin ? [{ to: "/superadmin", label: "Superadmin", icon: ShieldAlert }] : []),
  ];

  return (
    <header className="sticky top-0 z-40 w-full glass">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <SubizaLogo />
        <nav className="hidden md:flex items-center gap-1 text-sm font-medium">
          {navItems.map(({ to, label, icon: Icon, exact }) => (
            <Button key={to} variant="ghost" size="sm" asChild>
              <Link
                to={to}
                activeOptions={exact ? { exact: true } : undefined}
                activeProps={ACTIVE_PROPS}
              >
                <Icon className="size-4" /> {label}
              </Link>
            </Button>
          ))}
        </nav>
        <div className="flex items-center gap-1 sm:gap-2">
          <ThemeToggle />
          {user ? (
            <Button variant="outline" size="sm" onClick={() => signOut()}>
              <LogOut className="size-4" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
          ) : (
            <>
              <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex">
                <Link to="/auth" aria-label="Teacher login">
                  <LogIn className="size-4" />{" "}
                  <span className="hidden sm:inline">Teacher login</span>
                </Link>
              </Button>
              <Button size="sm" asChild className="bg-gradient-primary shadow-elegant">
                <Link to="/" aria-label="Join game">
                  <Gamepad2 className="size-4" />{" "}
                  <span className="hidden sm:inline">Join game</span>
                </Link>
              </Button>
            </>
          )}

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="size-11 md:hidden"
                aria-label="Open menu"
              >
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <SheetHeader>
                <SheetTitle>Menu</SheetTitle>
              </SheetHeader>
              <nav className="mt-6 flex flex-col gap-1 text-sm font-medium">
                {navItems.map(({ to, label, icon: Icon, exact }) => (
                  <Button
                    key={to}
                    variant="ghost"
                    className="justify-start"
                    asChild
                    onClick={() => setMenuOpen(false)}
                  >
                    <Link
                      to={to}
                      activeOptions={exact ? { exact: true } : undefined}
                      activeProps={ACTIVE_PROPS}
                    >
                      <Icon className="size-4" /> {label}
                    </Link>
                  </Button>
                ))}
                {user ? (
                  <Button
                    variant="outline"
                    className="mt-2 justify-start"
                    onClick={() => {
                      setMenuOpen(false);
                      signOut();
                    }}
                  >
                    <LogOut className="size-4" /> Sign out
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    className="mt-2 justify-start"
                    asChild
                    onClick={() => setMenuOpen(false)}
                  >
                    <Link to="/auth">
                      <LogIn className="size-4" /> Teacher login
                    </Link>
                  </Button>
                )}
              </nav>
              <div className="mt-4 flex items-center justify-between border-t border-border pt-4">
                <span className="text-sm text-muted">Theme</span>
                <ThemeToggle />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
